// src/pages/ScanScoreArticle.js

import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  GraduationCap, MapPin, Users, Award,
  ChevronDown, ChevronUp, ExternalLink,
  Share2, Check, Sparkles, Heart
} from 'lucide-react';
import '../styles/ScanScoreArticle.scss';

export default function ScanScoreArticle() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [copied, setCopied] = useState(false);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "How ScanScore (scanscore.app) Changed Everything for Prabhat Tara Vidya Niketan: A Real School's Assessment Experience",
    "description": "The real story of how teachers at Prabhat Tara Vidya Niketan (Goraul, Bihar) eliminated 15+ hours of manual paper checking using ScanScore's phone-based OMR camera scanning on plain A4 paper.",
    "author": {
      "@type": "School",
      "name": "Prabhat Tara Vidya Niketan",
      "url": "https://prabhattaravidyaniketan.in"
    },
    "publisher": {
      "@type": "EducationalOrganization",
      "name": "Prabhat Tara Vidya Niketan",
      "logo": {
        "@type": "ImageObject",
        "url": "https://prabhattaravidyaniketan.in/images/logo.jpg"
      }
    },
    "datePublished": "2026-11-04",
    "mainEntityOfPage": "https://prabhattaravidyaniketan.in/scanscore-assessment",
    "about": {
      "@type": "SoftwareApplication",
      "name": "ScanScore",
      "url": "https://scanscore.app",
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "Android, Mobile Web"
    }
  };

  const faqList = [
    {
      q: "Does our school need to buy special OMR sheets or an expensive optical scanner?",
      a: "No. That was our biggest hesitation at PTVN initially. Traditional optical scanners cost upwards of ₹1.5 Lakhs and require expensive 105+ GSM sheets shipped from major cities. With ScanScore (scanscore.app), you design the test on the app, download the standard A4 PDF sheet, and photocopy it directly in your school or local Xerox shop on ordinary 70-75 GSM paper for just 50 paise."
    },
    {
      q: "How does ScanScore perform in areas with unreliable internet or power cuts?",
      a: "It functions completely offline. In Goraul, intermittent electricity and weak mobile data are common. ScanScore processes sheet alignment, bubble fill detection, and scoring entirely on the phone's internal processor. Our teachers routinely grade full 50-student batches in Airplane mode."
    },
    {
      q: "How fast is the phone camera scanning in practice?",
      a: "Extraordinarily fast. With its auto-capture mode, the teacher simply holds the phone steady over the desk. As soon as an answer sheet is placed underneath, ScanScore captures and grades it in less than a second. Sliding the next sheet underneath immediately triggers the next score. A class of 45 students is finished in about 90 seconds."
    },
    {
      q: "How does it handle multiple question sets (Set A, Set B, Set C)?",
      a: "To prevent students from copying in packed examination halls, we distribute randomized Question Sets (A through J). ScanScore automatically reads the set bubble marked by the student and applies the matching answer key without teachers needing to sort the papers into separate piles first."
    },
    {
      q: "Can ScanScore apply negative marking for competitive exam coaching?",
      a: "Yes. For our senior students preparing for board exams, Olympiads, and competitive foundations (like NTSE, JEE, and NEET), ScanScore allows custom positive marks and fractional negative marks (such as -0.25, -0.33, or -0.5), as well as multi-correct answer options."
    },
    {
      q: "How can another school or coaching institute start using ScanScore?",
      a: "Simply visit scanscore.app on any web browser or download the mobile app. You can create an exam blueprint in less than 2 minutes, download the A4 OMR sheet, print copies, and start scanning immediately."
    }
  ];

  return (
    <div className="scanscore-experience-page">
      <Helmet>
        <title>How ScanScore (scanscore.app) Transformed Exam Checking at Prabhat Tara Vidya Niketan</title>
        <meta 
          name="description" 
          content="The authentic story of how Prabhat Tara Vidya Niketan (PTVN Goraul) replaced painful manual paper checking with ScanScore (scanscore.app). Instant phone OMR scanning on plain A4 paper." 
        />
        <meta 
          name="keywords" 
          content="ScanScore, scanscore.app, Prabhat Tara Vidya Niketan, PTVN Goraul, phone OMR scanner, OMR sheet scanner app, mobile OMR reader, school exam evaluation, teacher grading app, offline OMR grading, Bihar board exam preparation" 
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/scanscore-assessment" />
        
        {/* OpenGraph & Social */}
        <meta property="og:title" content="How ScanScore Transformed Exam Checking at Prabhat Tara Vidya Niketan" />
        <meta property="og:description" content="We used to check 300 papers till midnight with red ink. Here is how ScanScore (scanscore.app) changed everything for our school." />
        <meta property="og:url" content="https://prabhattaravidyaniketan.in/scanscore-assessment" />
        <meta property="og:type" content="article" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Breadcrumbs */}
      <nav className="experience-breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span className="crumb-sep">/</span>
        <Link to="/academics">Academics</Link>
        <span className="crumb-sep">/</span>
        <Link to="/exam-corner">Exam Corner</Link>
        <span className="crumb-sep">/</span>
        <span className="crumb-current">Our ScanScore Experience</span>
      </nav>

      {/* Hero Section */}
      <header className="experience-hero">
        <div className="hero-eyebrow-badge">
          <Sparkles size={14} /> Real School Story • From The Staff Room of PTVN Goraul
        </div>
        <h1>
          <span className="hero-quote-mark">“</span>We Used to Check 300 Papers Till Midnight With Red Ink. <br />
          Here is How <span className="hero-gradient-text">ScanScore (scanscore.app)</span> Changed Everything for Our School.<span className="hero-quote-mark">”</span>
        </h1>
        <p className="hero-lead-text">
          A first-hand reflection from the teachers, students, and academic board of <strong>Prabhat Tara Vidya Niketan (PTVN)</strong> on how phone-based OMR scanning on plain A4 paper rescued our faculty from burnout and unlocked weekly exam mastery.
        </p>

        <div className="hero-school-chip-row">
          <div className="school-chip">
            <GraduationCap size={16} /> Prabhat Tara Vidya Niketan
          </div>
          <div className="school-chip">
            <MapPin size={16} /> Goraul, Vaishali (Bihar)
          </div>
          <div className="school-chip">
            <Users size={16} /> 1,200+ Students • Estd. 1995
          </div>
          <div className="school-chip">
            <Award size={16} /> 30+ Years of Academic Excellence
          </div>
        </div>

        <div className="hero-action-buttons">
          <a 
            href="https://scanscore.app" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-hero-primary"
            title="Visit ScanScore Official Portal (scanscore.app)"
          >
            Visit ScanScore.app <ExternalLink size={16} />
          </a>
          <button 
            onClick={handleShare} 
            className="btn-hero-secondary"
            aria-label="Share this story"
          >
            {copied ? <Check size={16} color="#27ae60" /> : <Share2 size={16} />}
            {copied ? "Link Copied!" : "Share School Story"}
          </button>
        </div>
      </header>

      {/* Sticky Chapter Nav */}
      <nav className="story-nav-bar" aria-label="Article navigation">
        <div className="story-nav-inner">
          <a href="#staffroom-struggle" className="nav-pill">1. The Struggle Before</a>
          <a href="#the-discovery" className="nav-pill">2. The Discovery</a>
          <a href="#the-first-test" className="nav-pill">3. The First Day</a>
          <a href="#five-ways-eased" className="nav-pill">4. How It Eases Our Work</a>
          <a href="#teacher-voices" className="nav-pill">5. Teacher & Student Voices</a>
          <a href="#comparison" className="nav-pill">6. Side-by-Side Comparison</a>
          <a href="#how-to-start" className="nav-pill">7. 4 Steps for Any School</a>
          <a href="#faqs" className="nav-pill">8. FAQs</a>
        </div>
      </nav>

      {/* Main Story Content */}
      <div className="story-content-container">
        <main className="story-article-card">
          
          {/* Chapter 1 */}
          <section id="staffroom-struggle">
            <h2>Chapter 1: The Staff Room at 4:30 PM — The Silent Struggle Before ScanScore</h2>
            <p>
              If you visited the staff room of <strong>Prabhat Tara Vidya Niketan in Goraul</strong> on any Friday afternoon two years ago, you would have witnessed a familiar, exhausting sight. 
            </p>
            <p>
              Our teachers—who had already poured their heart and soul into teaching six back-to-back classes in the humid Bihar heat—would be sitting before towering stacks of question papers. With more than 1,200 students enrolled from Nursery to Class 10, a single monthly unit test or pre-board mock examination generated over <strong>15,000 individual multiple-choice questions</strong> that needed evaluation.
            </p>

            <div className="story-vivid-box">
              <span className="box-tag"><Heart size={14} /> The Teacher's Reality</span>
              <p>
                “You go home with a heavy shoulder bag stuffed with 250 sheets. Your family is having dinner, but you are sitting under the table fan with red ballpoint pens, checking circle by circle till 11:30 PM. After checking 40 papers, your eyes blur. You recalculate totals on the margin, scared that you might miscalculate by one mark and break a student's heart.”
              </p>
            </div>

            <p>
              The worst part wasn't the physical exhaustion. It was the <strong>delayed feedback loop</strong>.
            </p>
            <p>
              By the time a teacher manually checked 250 papers, recorded the marks into register ledgers, and handed the answer sheets back, an entire week had passed. The student had already forgotten what their doubt was on Question #14. The crucial educational moment—when curiosity and memory were alive—had evaporated.
            </p>
          </section>

          {/* Impact Stats */}
          <div className="ptvn-impact-stats">
            <div className="stat-pill-card">
              <div className="stat-num">15 hrs</div>
              <div className="stat-txt">Weekly Checking Saved</div>
            </div>
            <div className="stat-pill-card">
              <div className="stat-num">50 Paise</div>
              <div className="stat-txt">Cost per A4 Sheet</div>
            </div>
            <div className="stat-pill-card">
              <div className="stat-num">0.8 sec</div>
              <div className="stat-txt">Scan Speed per Paper</div>
            </div>
            <div className="stat-pill-card">
              <div className="stat-num">100%</div>
              <div className="stat-txt">Offline in Classroom</div>
            </div>
          </div>

          {/* Chapter 2 */}
          <section id="the-discovery">
            <h2>Chapter 2: Why We Couldn't Just Buy a Traditional OMR Machine</h2>
            <p>
              Like any school striving for excellence, our management initially explored purchasing a commercial Optical Mark Recognition (OMR) machine. But when the sales quotes arrived, reality hit us hard:
            </p>
            <ul>
              <li>
                <strong>Shocking Capital Cost:</strong> Hardware OMR scanners were quoted between <strong>₹1,50,000 and ₹2,80,000</strong>.
              </li>
              <li>
                <strong>Proprietary Sheet Trap:</strong> They required thick, specialized 105+ GSM offset-printed sheets costing ₹3 to ₹5 per copy. To conduct regular 25-question weekly quizzes across our classes, paper costs alone would have exceeded ₹60,000 every term!
              </li>
              <li>
                <strong>Maintenance Nightmares:</strong> Paper feeds jammed constantly in dusty rural weather, optical lenses required technician recalibration, and the software was locked to a desktop with a physical USB license key.
              </li>
            </ul>
            <p>
              It was completely impractical for daily classroom education. We shelved the idea and went back to the red pen.
            </p>
          </section>

          {/* Chapter 3 */}
          <section id="the-first-test">
            <h2>Chapter 3: The Day ScanScore Arrived at Goraul</h2>
            <p>
              One morning during our academic review meeting, our tech coordinator brought a phone to the table and showed us <a href="https://scanscore.app" target="_blank" rel="noopener noreferrer"><strong>ScanScore (scanscore.app)</strong></a>.
            </p>
            <p>
              <em>“Sir,”</em> he said, <em>“we don't need any machine. We don't even need special paper. We just use our existing school photocopier and our mobile phones.”</em>
            </p>
            <p>
              Many of our senior faculty members were naturally skeptical. Could an everyday smartphone camera really read tiny pencil and ballpen bubbles with the precision of a ₹2 Lakh scanner?
            </p>
            <p>
              We decided to put it to the test with Class 10's 50-question Science Pre-Board Mock.
            </p>
            <p>
              We printed 45 sheets on <strong>ordinary 70 GSM A4 photocopy paper</strong> from our school office printer. The students filled their roll numbers and bubbled their answers.
            </p>
            <p>
              When the bell rang, the teacher stood at the teacher's desk, opened <a href="https://scanscore.app" target="_blank" rel="noopener noreferrer">ScanScore</a> on his phone, and held it above the first paper.
            </p>
            <p>
              <strong>*Click*. In 0.8 seconds, the phone screen flashed.</strong>
            </p>
            <p>
              Green circular rings instantly encircled every correct answer. Red circular rings highlighted the mistakes. The exact score—42 out of 50—appeared on screen with zero delay.
            </p>
            <p>
              He slid the next paper in. *Click*. Graded. Next paper. *Click*. Graded.
            </p>
            <p>
              <strong>In under two minutes, all 45 student papers were completely evaluated.</strong> Not a single clerical mistake. No manual summing. The entire class merit list was automatically organized by roll number on the screen.
            </p>
            <p>
              The entire staff room fell silent. We knew in that exact moment that our school would never look back.
            </p>
          </section>

          {/* Chapter 4 */}
          <section id="five-ways-eased">
            <h2>Chapter 4: The 5 Real Ways ScanScore Eases Work for PTVN Teachers</h2>
            <p>
              <a href="https://scanscore.app" target="_blank" rel="noopener noreferrer">ScanScore</a> is not just software; it is an everyday lifesaver for our educators. Here is how it fundamentally eases our work:
            </p>

            <div className="work-ease-cards">
              <div className="ease-card">
                <div className="ease-card-header">
                  <div className="card-badge-num">1</div>
                  <h4>Our Evenings Belong to Our Families Again</h4>
                </div>
                <p>
                  Instead of spending 12 to 15 hours every weekend hunched over exam papers, teachers finish grading in their free period or before leaving the school gate. Teachers return home refreshed, energized, and ready to plan creative lessons instead of nursing red-ink migraines.
                </p>
              </div>

              <div className="ease-card">
                <div className="ease-card-header">
                  <div className="card-badge-num">2</div>
                  <h4>Printed on Normal Paper from our Goraul Xerox Machine</h4>
                </div>
                <p>
                  We don't order special sheets from Patna or Kolkata. We generate the template on <a href="https://scanscore.app" target="_blank" rel="noopener noreferrer">scanscore.app</a>, print one master copy, and run it through our standard office photocopier. Each sheet costs approximately <strong>50 paise</strong>, making weekly 20-question practice tests completely affordable.
                </p>
              </div>

              <div className="ease-card">
                <div className="ease-card-header">
                  <div className="card-badge-num">3</div>
                  <h4>100% Offline: Zero Dependency on School Internet</h4>
                </div>
                <p>
                  In rural Bihar, electricity and internet signals fluctuate. ScanScore performs all computer vision, angle alignment, and scoring locally on the device's chip. Our teachers regularly scan in airplane mode inside classrooms without waiting for a spinning loading wheel.
                </p>
              </div>

              <div className="ease-card">
                <div className="ease-card-header">
                  <div className="card-badge-num">4</div>
                  <h4>No Cheating, No Sorting: Sets A, B, C, D Auto-Detected</h4>
                </div>
                <p>
                  To eliminate classroom copying, we distribute randomized Question Sets (Set A, Set B, Set C, Set D). In the past, separating 200 papers into four distinct piles before checking took 30 minutes. ScanScore detects the student's marked set bubble automatically and applies the matching answer key on the fly.
                </p>
              </div>

              <div className="ease-card">
                <div className="ease-card-header">
                  <div className="card-badge-num">5</div>
                  <h4>Instant Class Discussion While Memory is Fresh</h4>
                </div>
                <p>
                  Because grading takes only 2 minutes, teachers immediately review results with students during the very same class period. ScanScore shows that 68% of the class failed Question #18 (e.g. Optics focal length formula). The teacher turns to the board and clarifies the concept immediately, transforming a test from an audit into an active learning experience.
                </p>
              </div>
            </div>
          </section>

          {/* Chapter 5 */}
          <section id="teacher-voices">
            <h2>Chapter 5: Real Voices from Prabhat Tara Vidya Niketan</h2>
            <p>
              Here is what our educators and students have to say about the transformation:
            </p>

            <div className="testimonials-voice-grid">
              <div className="voice-card">
                <p className="voice-quote">
                  I have taught Class 9 and 10 Science for 14 years. Checking board preparatory papers was something we all dreaded. With ScanScore, I check 90 sheets in 5 minutes. The green and red rings on the scanned sheet mean students never doubt their marks. It has brought radical transparency to our classrooms.
                </p>
                <div className="voice-author-bar">
                  <div className="voice-avatar">MK</div>
                  <div className="voice-details">
                    <div className="voice-name">Manoj Kumar</div>
                    <div className="voice-role">HOD Science & Board Exam Coordinator</div>
                  </div>
                </div>
              </div>

              <div className="voice-card">
                <p className="voice-quote">
                  During Parent-Teacher Meetings, parents are amazed. Instead of just showing a raw score on a report card, we show them topic-level breakdowns exported from ScanScore. Parents can clearly see whether their child needs help in Algebra or Trigonometry.
                </p>
                <div className="voice-author-bar">
                  <div className="voice-avatar">AS</div>
                  <div className="voice-details">
                    <div className="voice-name">Anjali Sinha</div>
                    <div className="voice-role">Senior Mathematics Faculty</div>
                  </div>
                </div>
              </div>

              <div className="voice-card">
                <p className="voice-quote">
                  Earlier, we had to wait a full week to get our unit test marks. By then, we were already onto the next chapter. Now, Sir checks our papers right in front of us before the period ends. It feels like we are writing a real JEE/NEET mock exam in Patna!
                </p>
                <div className="voice-author-bar">
                  <div className="voice-avatar">AK</div>
                  <div className="voice-details">
                    <div className="voice-name">Ayush Kumar</div>
                    <div className="voice-role">Class 10 Board Aspirant</div>
                  </div>
                </div>
              </div>

              <div className="voice-card">
                <p className="voice-quote">
                  ScanScore (scanscore.app) democratized modern educational technology for our school in Goraul. It gave our students and teachers the assessment speed of top metropolitan coaching institutes without charging our school or parents a fortune.
                </p>
                <div className="voice-author-bar">
                  <div className="voice-avatar">PR</div>
                  <div className="voice-details">
                    <div className="voice-name">Principal's Office</div>
                    <div className="voice-role">Prabhat Tara Vidya Niketan</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Chapter 6 */}
          <section id="comparison">
            <h2>Chapter 6: Side-by-Side Comparison: Old Way vs. ScanScore</h2>
            <p>
              Here is an honest assessment of how the three options compare in a real school environment:
            </p>

            {/* Desktop Table */}
            <div className="comparison-table-wrapper">
              <table aria-label="Comparison Table">
                <thead>
                  <tr>
                    <th>Parameter</th>
                    <th>The Old Way (Manual Red Pen)</th>
                    <th>Bulky OMR Scanner</th>
                    <th className="col-scanscore">Our ScanScore Experience</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Hardware Required</strong></td>
                    <td>Red ballpoint pens</td>
                    <td>Expensive machine (₹1.5L+)</td>
                    <td className="col-scanscore">Teacher's mobile phone camera</td>
                  </tr>
                  <tr>
                    <td><strong>Paper Cost</strong></td>
                    <td>Normal notebook / test sheets</td>
                    <td>₹3 to ₹5 per proprietary sheet</td>
                    <td className="col-scanscore"><strong>50 paise</strong> on regular A4 copy paper</td>
                  </tr>
                  <tr>
                    <td><strong>Speed for 100 Papers</strong></td>
                    <td>4 to 6 exhausting hours</td>
                    <td>15 minutes (with frequent jams)</td>
                    <td className="col-scanscore"><strong>Under 3 minutes</strong> with continuous auto-scan</td>
                  </tr>
                  <tr>
                    <td><strong>Offline Capability</strong></td>
                    <td>Yes</td>
                    <td>Requires desktop tethering</td>
                    <td className="col-scanscore"><strong>100% Offline</strong> on-device edge computing</td>
                  </tr>
                  <tr>
                    <td><strong>Question Sets (A-J)</strong></td>
                    <td>30 minutes manual sorting</td>
                    <td>Complicated software settings</td>
                    <td className="col-scanscore"><strong>Auto-detected</strong> from student bubble</td>
                  </tr>
                  <tr>
                    <td><strong>Student Feedback</strong></td>
                    <td>Delayed by 5 to 7 days</td>
                    <td>Raw score printout</td>
                    <td className="col-scanscore"><strong>Instant green/red visual rings</strong> & ranking</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Cards (100% Responsive) */}
            <div className="comparison-cards-mobile">
              <div className="mobile-comp-card">
                <div className="comp-card-title">1. Hardware & Setup Cost</div>
                <div className="comp-item">
                  <span className="comp-label">Old Way:</span>
                  <span className="comp-value">Manual red pens (15+ hours)</span>
                </div>
                <div className="comp-item">
                  <span className="comp-label">Hardware Scanner:</span>
                  <span className="comp-value">₹1,50,000+ machine + proprietary drivers</span>
                </div>
                <div className="comp-item">
                  <span className="comp-label">ScanScore (Our Way):</span>
                  <span className="comp-value winner">✓ Zero extra hardware. Any smartphone camera.</span>
                </div>
              </div>

              <div className="mobile-comp-card">
                <div className="comp-card-title">2. Paper & Stationery Cost</div>
                <div className="comp-item">
                  <span className="comp-label">Old Way:</span>
                  <span className="comp-value">Printed question papers</span>
                </div>
                <div className="comp-item">
                  <span className="comp-label">Hardware Scanner:</span>
                  <span className="comp-value">₹3 to ₹5 per pre-printed thick sheet</span>
                </div>
                <div className="comp-item">
                  <span className="comp-label">ScanScore (Our Way):</span>
                  <span className="comp-value winner">✓ 50 paise on plain A4 copy paper.</span>
                </div>
              </div>

              <div className="mobile-comp-card">
                <div className="comp-card-title">3. Evaluation Speed (100 Papers)</div>
                <div className="comp-item">
                  <span className="comp-label">Old Way:</span>
                  <span className="comp-value">5 hours of stressful manual counting</span>
                </div>
                <div className="comp-item">
                  <span className="comp-label">Hardware Scanner:</span>
                  <span className="comp-value">15 minutes with frequent feeder jams</span>
                </div>
                <div className="comp-item">
                  <span className="comp-label">ScanScore (Our Way):</span>
                  <span className="comp-value winner">✓ Under 3 minutes with batch camera scanning.</span>
                </div>
              </div>

              <div className="mobile-comp-card">
                <div className="comp-card-title">4. Question Sets (A through J)</div>
                <div className="comp-item">
                  <span className="comp-label">Old Way:</span>
                  <span className="comp-value">30 minutes separating piles manually</span>
                </div>
                <div className="comp-item">
                  <span className="comp-label">Hardware Scanner:</span>
                  <span className="comp-value">Complex software batch templates</span>
                </div>
                <div className="comp-item">
                  <span className="comp-label">ScanScore (Our Way):</span>
                  <span className="comp-value winner">✓ Automatic set recognition in milliseconds.</span>
                </div>
              </div>
            </div>
          </section>

          {/* Chapter 7 */}
          <section id="how-to-start">
            <h2>Chapter 7: How Any School Can Start With ScanScore in 4 Simple Steps</h2>
            <p>
              If your school or tuition center is still drowning in manual paper correction, you don't need a committee meeting or technical consultant. Here is how simple it is:
            </p>

            <div className="simple-steps-grid">
              <div className="step-item-card">
                <span className="step-pill">Step 1</span>
                <h4>Set Up Exam at scanscore.app</h4>
                <p>
                  Visit <a href="https://scanscore.app" target="_blank" rel="noopener noreferrer">scanscore.app</a> on your phone or laptop. Enter the number of questions (10 to 200), select marking options (A–D or A–E), and enter the answer key.
                </p>
              </div>

              <div className="step-item-card">
                <span className="step-pill">Step 2</span>
                <h4>Print Plain A4 OMR Sheets</h4>
                <p>
                  Download the generated OMR sheet PDF. Print a master sheet and photocopy as many copies as you need on your regular office A4 printer.
                </p>
              </div>

              <div className="step-item-card">
                <span className="step-pill">Step 3</span>
                <h4>Administer the Test</h4>
                <p>
                  Students bubble their roll numbers and answers using any normal blue/black ballpoint pen or pencil.
                </p>
              </div>

              <div className="step-item-card">
                <span className="step-pill">Step 4</span>
                <h4>Batch Scan With Your Phone</h4>
                <p>
                  Open ScanScore camera mode. Hold the phone over each paper. It grades each sheet in 0.8 seconds and exports the complete class rank list directly to Excel or PDF.
                </p>
              </div>
            </div>
          </section>

          {/* Chapter 8: FAQs */}
          <section id="faqs">
            <h2>Chapter 8: Frequently Asked Questions</h2>
            <div className="faq-accordion-block">
              {faqList.map((faq, index) => (
                <div 
                  key={index} 
                  className={`faq-card ${activeFaq === index ? 'open' : ''}`}
                >
                  <button 
                    className="faq-btn" 
                    onClick={() => toggleFaq(index)}
                    aria-expanded={activeFaq === index}
                  >
                    <span>{faq.q}</span>
                    {activeFaq === index ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                  {activeFaq === index && (
                    <div className="faq-body">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* End Call to Action */}
          <section className="story-end-cta">
            <span className="cta-badge">A Message From Prabhat Tara Vidya Niketan</span>
            <h3>Give Your Teachers Their Evenings Back</h3>
            <p>
              ScanScore helped our teachers in Goraul eliminate grading burnout, increase test frequency, and prepare our students for board exam toppers. Experience it for yourself.
            </p>
            <div className="cta-btn-group">
              <a 
                href="https://scanscore.app" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-cta-launch"
              >
                Try ScanScore Free at scanscore.app <ExternalLink size={16} style={{ marginLeft: '4px' }} />
              </a>
              <Link to="/contact-us" className="btn-cta-contact">
                Contact PTVN Administration &rarr;
              </Link>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
